import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";

import LeadsAddedReportTable from "components/Common/LeadsAddedReportTable";
import Loaders from "components/Common/Loaders";
import {
    AsyncClientSelect,
    AsyncUserSelect,
    StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import LeadsAddedStatusCandidatesModal from "pages/AllCommonModal/LeadsAddedStatusCandidatesModal/leads-added-status-candidates-modal";
import {
    fetchLeadsAddedJobCategoryOptions,
    fetchLeadsAddedTeamOptions,
    submitLeadsAddedReportFilterData,
} from "model/LeadsAddedReportModel";

const BASE_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
];

const TRAILING_TABLE_COLUMNS = [
    { Header: "Email sent count", accessor: "emailSentCount" },
    { Header: "Last Day when email was sent", accessor: "lastDayWhenEmailWasSent" },
];

const normalizeStatusKey = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const buildStatusAccessor = (index) => `status_${index}`;

const extractStatusColumnDefinitions = (rows = []) => {
    const list = Array.isArray(rows) ? rows : [];
    const firstRowWithStatusColumns = list.find(
        (row) => Array.isArray(row?.statusColumns) && row.statusColumns.length
    );
    if (!firstRowWithStatusColumns) return [];

    return firstRowWithStatusColumns.statusColumns
        .map((statusColumn = {}) => {
            const statusKey = String(statusColumn?.statusKey ?? "").trim();
            return {
                statusKey,
                statusLabel: String(statusColumn?.statusLabel ?? statusKey).trim(),
            };
        })
        .filter((definition) => definition.statusKey);
};

const buildTableColumns = (statusColumnDefinitions = []) => {
    const statusColumns = (Array.isArray(statusColumnDefinitions)
        ? statusColumnDefinitions
        : []
    ).map((definition, index) => ({
        Header: definition.statusLabel,
        accessor: buildStatusAccessor(index),
        statusKey: definition.statusKey,
        isStatusCount: true,
    }));

    return [...BASE_TABLE_COLUMNS, ...statusColumns, ...TRAILING_TABLE_COLUMNS];
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const leadsAddedReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const formatDateValue = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const mapRowsFromApi = (rows = [], statusColumnDefinitions = []) => {
    const definitions = Array.isArray(statusColumnDefinitions) ? statusColumnDefinitions : [];
    const statusKeyToIndex = new Map(
        definitions.map((definition, index) => [
            normalizeStatusKey(definition.statusKey),
            index,
        ])
    );

    return (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const mappedRow = {
            ...item,
            sn: item?.sn ?? item?.serialNumber ?? item?.sN ?? index + 1,
            client: item?.client ?? item?.clientName ?? "",
            clientId: item?.clientId ?? "",
            emailSentCount: item?.emailSentCount ?? item?.email_send_count ?? "",
            lastDayWhenEmailWasSent:
                item?.lastEmailSentRelative ??
                item?.lastDayWhenEmailWasSent ??
                item?.last_day_when_email_sent ??
                formatDateValue(item?.lastEmailSentIso ?? item?.lastEmailSentDate),
        };

        definitions.forEach((_, labelIndex) => {
            mappedRow[buildStatusAccessor(labelIndex)] = "";
        });

        (Array.isArray(item?.statusColumns) ? item.statusColumns : []).forEach(
            (statusColumn = {}) => {
                const labelIndex = statusKeyToIndex.get(
                    normalizeStatusKey(statusColumn?.statusKey)
                );
                if (labelIndex === undefined) return;
                mappedRow[buildStatusAccessor(labelIndex)] =
                    statusColumn?.count ?? statusColumn?.value ?? "";
            }
        );

        return mappedRow;
    });
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(responseData?.data)
                ? responseData.data
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : [];

    return { rows };
};

const LeadsAddedReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        jobCategoryId: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        teamId: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
    });
    const [jobCategoryOptions, setJobCategoryOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [statusColumnDefinitions, setStatusColumnDefinitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [statusModalContext, setStatusModalContext] = useState(null);
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => reportData, [reportData]);
    const tableColumns = useMemo(
        () => buildTableColumns(statusColumnDefinitions),
        [statusColumnDefinitions]
    );

    const statusCandidatesFilterPayload = useMemo(
        () => ({
            categoryId: strOrNull(filterForm?.jobCategoryId),
            dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
            dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
        }),
        [filterForm?.fromDate, filterForm?.toDate, filterForm?.jobCategoryId]
    );

    useEffect(() => {
        const loadFilterOptions = async () => {
            const [jobCategories, teams] = await Promise.all([
                fetchLeadsAddedJobCategoryOptions(),
                fetchLeadsAddedTeamOptions(),
            ]);
            setJobCategoryOptions(Array.isArray(jobCategories) ? jobCategories : []);
            setTeamOptions(Array.isArray(teams) ? teams : []);
        };
        loadFilterOptions();
    }, []);

    const buildFilterPayload = () => ({
        dateFrom: statusCandidatesFilterPayload.dateFrom,
        dateTo: statusCandidatesFilterPayload.dateTo,
        categoryId: statusCandidatesFilterPayload.categoryId,
        clientId: strOrNull(filterForm?.clientId),
        teamId: strOrNull(filterForm?.teamId),
        userId: strOrNull(filterForm?.userId),
    });

    const getLeadsAddedReportList = async () => {
        try {
            await leadsAddedReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitLeadsAddedReportFilterData(buildFilterPayload());
            const { rows } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setStatusColumnDefinitions([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const nextStatusColumnDefinitions = extractStatusColumnDefinitions(rows);
            setStatusColumnDefinitions(nextStatusColumnDefinitions);
            setReportData(mapRowsFromApi(rows, nextStatusColumnDefinitions));
        } catch (error) {
            toast.error(error?.message || "Unable to load leads added report");
            setReportData([]);
            setStatusColumnDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleJobCategoryChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            jobCategoryId: option?.value || "",
        }));
    };

    const handleTeamChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            teamId: option?.value || "",
        }));
    };

    const handleStatusCountClick = (row = {}, column = {}) => {
        const clientId = String(row?.clientId ?? "").trim();
        const statusKey = String(column?.statusKey ?? "").trim();
        if (!clientId || !statusKey) {
            toast.error("Unable to open candidate list for this count.");
            return;
        }

        setStatusModalContext({
            clientId,
            clientName: row?.client ?? "",
            statusKey,
            statusLabel: column?.Header ?? "",
        });
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setStatusModalContext(null);
    };

    return (
        <React.Fragment>
            <div className="page-content leads-added-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Leads Added Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">Job Category</span>
                                        <StaticFilterSelect
                                            options={jobCategoryOptions}
                                            placeholder="Job Category"
                                            value={filterForm?.jobCategoryId || ""}
                                            onChange={handleJobCategoryChange}
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`leads-added-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportAllStatuses"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">Team</span>
                                        <StaticFilterSelect
                                            options={teamOptions}
                                            placeholder="Select Team"
                                            value={filterForm?.teamId || ""}
                                            onChange={handleTeamChange}
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span className="manage-user-filter-label">User</span>
                                        <AsyncUserSelect
                                            key={`leads-added-user-${filterForm?.userId || ""}`}
                                            pageName="LeadsAddedReport"
                                            value={filterForm?.userId || ""}
                                            initialOption={buildUserAsyncFilterInitialOption(
                                                filterForm?.userId,
                                                filterForm[getUserAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyUserAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select User"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getLeadsAddedReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <LeadsAddedReportTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    onStatusCountClick={handleStatusCountClick}
                                    pageName="LeadsAddedReport"
                                />
                            )}
                        </CardBody>
                    </Card>

                    <LeadsAddedStatusCandidatesModal
                        isOpen={statusModalOpen}
                        onClose={closeStatusModal}
                        clientId={statusModalContext?.clientId}
                        clientName={statusModalContext?.clientName}
                        statusKey={statusModalContext?.statusKey}
                        statusLabel={statusModalContext?.statusLabel}
                        filterPayload={statusCandidatesFilterPayload}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

LeadsAddedReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(LeadsAddedReport);
